import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useLanguage } from '../../utils/LanguageContext';
import { useCart } from '../../utils/CartContext'; // ADDED
import { getLocalized } from '../../utils/languageUtils';
import '../../styles/cart.css';
import { ROUTES } from '../../routes/path';

const Cart = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { refreshCart } = useCart(); // ADDED - to update global cart count

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  /* ---------------- FETCH CART ---------------- */

  const fetchCart = () => {
    return api.get('get/my/trees/')
      .then(res => {
        setCart(res.data || []);
        refreshCart(); // ADDED - update global cart count
        return res.data;
      })
      .catch(err => {
        console.error('Failed to load cart', err);
        setCart([]);
        return [];
      });
  };

  useEffect(() => {
    fetchCart().finally(() => setLoading(false));
  }, []);

  /* ---------------- CART ACTIONS ---------------- */



  const handleQuantityChange = (item, delta) => {
    setUpdating(prev => ({ ...prev, [`qty-${item.id}`]: true }));

    api.post('update/cart/', {
      bucket_id: item.id,
      delta: delta,
    })
      .then(() => {
        fetchCart(); // This now includes refreshCart() inside
      })
      .catch(err => {
        console.error('Failed to update quantity', err);
      })
      .finally(() => {
        setUpdating(prev => ({ ...prev, [`qty-${item.id}`]: false }));
      });
  };

  const handleRemove = (item) => {
    if (!window.confirm(t('cart_confirm_remove') || 'Remove this item from cart?')) {
      return;
    }

    setUpdating(prev => ({ ...prev, [`remove-${item.id}`]: true }));

    api.post('remove/cart/', {
      bucket_id: item.id,
    })
      .then(() => {
        fetchCart(); // This now includes refreshCart() inside
      })
      .catch(err => {
        console.error('Failed to remove item', err);
      })
      .finally(() => {
        setUpdating(prev => ({ ...prev, [`remove-${item.id}`]: false }));
      });
  };

  /* ---------------- TOTALS CALCULATION ---------------- */

  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;

    cart.forEach(item => {
      const price = parseFloat(item.product.price) || 0;
      const discount = parseFloat(item.product.discount) || 0;
      const itemSubtotal = price * item.count;
      const itemDiscount = (itemSubtotal * discount) / 100;

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
    });

    const total = subtotal - totalDiscount;

    return { subtotal, totalDiscount, total };
  };

  const { subtotal, totalDiscount, total } = calculateTotals();



  const handleCheckout = () => {
    navigate(`/${ROUTES.REVIEW_ORDER}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="cart-page-container">
        <div className="cart-loading">
          <div className="loading-icon">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h2>{t('shop_loading')}</h2>
        </div>
      </div>
    );
  }

  // Empty cart state
  if (!cart || cart.length === 0) {
    return (
      <div className="cart-empty-container">
        <div className="cart-empty-content">
          <div className="cart-empty-icon">
            <img src="/images/stickers/basket-sprout.webp" alt="" />
          </div>
          <h2 className="cart-empty-title">{t('cart_empty_title')}</h2>
          <p className="cart-empty-text">{t('cart_empty_text')}</p>
          <button
            className="btn-browse-trees"
            onClick={() => navigate(`/${ROUTES.SHOP}`)}
          >
            <i className="fas fa-tree"></i> {t('cart_btn_browse')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page-container">
      <div className="cart-content-wrapper">
        {/* Left Column - Cart Items */}
        <div className="cart-items-section">
          {/* Page Header */}
          <div className="cart-header">
            <h1 className="cart-title">
              <i className="fas fa-shopping-cart"></i> {t('cart_title')}
            </h1>
            <p className="cart-count">
              {cart.length} {cart.length === 1 ? t('cart_item_single') : t('cart_item_plural')}
            </p>
          </div>

          {/* Info Banners */}
          <div className="cart-banners">
            <div className="cart-banner info">
              <i className="fas fa-info-circle"></i>
              <span>{t('cart_banner_text')}</span>
            </div>
            <div className="cart-banner shipping">
              <i className="fas fa-truck"></i>
              <span>{t('cart_shipping')}</span>
            </div>
          </div>

          {/* Cart Items List */}
          <div className="cart-items-list">
            {cart.map((item) => {
              const itemPrice = parseFloat(item.product.price) || 0;
              const itemDiscount = parseFloat(item.product.discount) || 0;
              const itemSubtotal = itemPrice * item.count;
              const itemDiscountAmount = (itemSubtotal * itemDiscount) / 100;
              const itemTotal = itemSubtotal - itemDiscountAmount;

              const isUpdating = updating[`qty-${item.id}`] || updating[`remove-${item.id}`];

              return (
                <div key={item.id} className={`cart-item-card ${isUpdating ? 'updating' : ''}`}>
                  {/* Item Header */}
                  <div className="cart-item-header">
                    <div className="cart-item-image">
                      {item.product?.tree?.image ? (
                        <img
                          src={item.product.tree.image}
                          alt={getLocalized(item.product.tree, language, 'name') || 'Tree'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-size="40" text-anchor="middle" dy=".3em"%3E🌳%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div className="image-placeholder">
                          <i className="fas fa-tree"></i>
                        </div>
                      )}
                      {itemDiscount > 0 && (
                        <div className="discount-badge">-{itemDiscount}%</div>
                      )}
                    </div>

                    <div className="cart-item-info">
                      <h3 className="cart-item-name">
                        {getLocalized(item.product.tree, language, 'name') || 'Tree'}
                      </h3>
                      <p className="cart-item-category">
                        {item.product.tree?.family?.name || 'Native Tree'}
                      </p>
                      <div className="cart-item-price-info">
                        {itemDiscount > 0 ? (
                          <>
                            <span className="price-original">{itemPrice.toLocaleString()} UZS</span>
                            <span className="price-discounted">{(itemPrice - (itemPrice * itemDiscount / 100)).toLocaleString()} UZS</span>
                          </>
                        ) : (
                          <span className="price-current">{itemPrice.toLocaleString()} UZS</span>
                        )}
                      </div>
                    </div>

                    <button
                      className="cart-item-remove"
                      onClick={() => handleRemove(item)}
                      disabled={isUpdating}
                      aria-label="Remove item"
                    >
                      {updating[`remove-${item.id}`] ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-trash"></i>
                      )}
                    </button>
                  </div>

                  {/* Customize Section */}
                  <div className="cart-item-customize">
                    <h4 className="customize-title">
                      <i className="fas fa-sliders-h"></i> {t('cart_customize')}
                    </h4>

                    <div className="customize-controls">
                      {/* Quantity Selector */}
                      <div className="control-group">
                        <label className="control-label">
                          <i className="fas fa-hashtag"></i> {t('cart_qty')}
                        </label>
                        <div className="quantity-controls">
                          <button
                            className="qty-btn"
                            onClick={() => handleQuantityChange(item, -1)}
                            disabled={item.count <= 1 || isUpdating}
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                          <span className="qty-value">
                            {updating[`qty-${item.id}`] ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              item.count
                            )}
                          </span>
                          <button
                            className="qty-btn"
                            onClick={() => handleQuantityChange(item, 1)}
                            disabled={isUpdating}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Item Total */}
                  <div className="cart-item-total">
                    <div className="total-breakdown">
                      {itemDiscount > 0 && (
                        <>
                          <div className="total-row subtotal">
                            <span className="total-label">{t('cart_subtotal')}</span>
                            <span className="total-value">{itemSubtotal.toLocaleString()} UZS</span>
                          </div>
                          <div className="total-row discount">
                            <span className="total-label">{t('cart_discount')} ({itemDiscount}%)</span>
                            <span className="total-value">-{itemDiscountAmount.toLocaleString()} UZS</span>
                          </div>
                        </>
                      )}
                      <div className="total-row total">
                        <span className="total-label">{t('cart_item_total')}</span>
                        <span className="total-value">{itemTotal.toLocaleString()} UZS</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Continue Shopping Button */}
          <button
            className="btn-continue-shopping"
            onClick={() => navigate(`/${ROUTES.SHOP}`)}
          >
            <i className="fas fa-arrow-left"></i> {t('cart_continue')}
          </button>
        </div>

        {/* Right Column - Order Summary */}
        <div className="cart-summary-section">
          <div className="cart-summary-card sticky">
            <h2 className="summary-title">
              <i className="fas fa-receipt"></i>
              {t('cart_summary_title')}
            </h2>

            <div className="summary-breakdown">
              <div className="summary-row">
                <span className="summary-label">{t('cart_subtotal')}</span>
                <span className="summary-value">{subtotal.toLocaleString()} UZS</span>
              </div>

              {totalDiscount > 0 && (
                <div className="summary-row discount">
                  <span className="summary-label">
                    <i className="fas fa-tag"></i> {t('cart_discount')}
                  </span>
                  <span className="summary-value discount-value">
                    -{totalDiscount.toLocaleString()} UZS
                  </span>
                </div>
              )}

              <div className="summary-row">
                <span className="summary-label">{t('cart_service')}</span>
                <span className="summary-value free">{t('cart_free')}</span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-row total">
                <span className="summary-label">{t('cart_total')}</span>
                <span className="summary-value total-value">
                  {total.toLocaleString()} UZS
                </span>
              </div>
            </div>

            {totalDiscount > 0 && (
              <div className="savings-badge">
                <i className="fas fa-piggy-bank"></i>
                <span>{t('cart_you_save')} {totalDiscount.toLocaleString()} UZS!</span>
              </div>
            )}

            <button
              className="btn-checkout"
              onClick={handleCheckout}
            >
              <i className="fas fa-lock"></i> {t('cart_checkout')}
            </button>

            <div className="secure-checkout-badge">
              <i className="fas fa-shield-alt"></i>
              <span>{t('cart_secure')}</span>
            </div>
          </div>

          {/* Additional Info Cards */}
          <div className="cart-info-cards">
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-truck"></i>
              </div>
              <div className="info-content">
                <h4>{t('cart_info_planting')}</h4>
                <p>{t('cart_info_planting_desc')}</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-shield-check"></i>
              </div>
              <div className="info-content">
                <h4>{t('cart_info_quality')}</h4>
                <p>{t('cart_info_quality_desc')}</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-headset"></i>
              </div>
              <div className="info-content">
                <h4>{t('cart_info_support')}</h4>
                <p>{t('cart_info_support_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;